// client/src/services/aiReviewChat.ts

export interface ChatReturn {
  returnId:    string;
  returnName:  string;
  entityType:  string;
  taxYear:     string;
  description?: string;
  version?:    string | null;
}

export interface ReturnMeta {
  RETURN?:    string;
  'EIN/SSN'?: string;
  Type?:      string;
  TaxYear?:   string;
  ClientID?:  string;
  [key: string]: string | undefined;
}

export interface ChatLoadResult {
  returnId:   string;
  returnMeta: ReturnMeta;
  viewNames:  string[];
  fromCache:  boolean;
}

export interface ChatMessage {
  role:    'user' | 'assistant';
  content: string;
}

export interface ChatMessageResult {
  content:           string;
  scopedViews:       string[] | null;
  priorYearIncluded: boolean;
  inputTokens:       number;
  outputTokens:      number;
  estimatedCost:     number;
}

async function apiFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: 'include', ...init });
  if (res.status === 401) {
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/.auth/login/aad?prompt=login&post_login_redirect_uri=${returnTo}`;
    // Never resolves — browser is navigating away
    return new Promise(() => {});
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as any).error || `Server error ${res.status}`);
  }
  return res.json();
}

export async function lookupReturns(clientCode: string, taxYear: string): Promise<ChatReturn[]> {
  const params = new URLSearchParams({ clientCode, taxYear });
  const data = await apiFetch<{ returns: ChatReturn[] }>(`/api/ai-review/chat-lookup?${params}`);
  return data.returns;
}

export interface SharedChat {
  shareId:    string;
  messages:   ChatMessage[];
  returnMeta: ReturnMeta;
  sharedBy:   string;
  sharedAt:   string;
}

export async function shareChat(params: {
  messages: ChatMessage[];
  returnMeta: ReturnMeta;
  to: string;
  subject: string;
  body: string;
  bcc: string;
}): Promise<{ shareId: string; shareUrl: string }> {
  return apiFetch('/api/ai-review/chat-share', {
    method:  'POST',
    headers: { 'content-type': 'application/json' },
    body:    JSON.stringify(params),
  });
}

export async function loadSharedChat(shareId: string): Promise<SharedChat> {
  return apiFetch(`/api/ai-review/chat-share/${encodeURIComponent(shareId)}`);
}

export async function polishSnippets(params: {
  snippets: string[];
  clientName: string;
  entityType?: string;
  taxYear?: string;
}): Promise<{ markdown: string }> {
  return apiFetch('/api/ai-review/chat-polish', {
    method:  'POST',
    headers: { 'content-type': 'application/json' },
    body:    JSON.stringify(params),
  });
}

export async function generateDeliverablePdf(params: {
  markdown: string;
  clientName: string;
  entityType?: string;
  taxYear?: string;
}): Promise<Blob> {
  const res = await fetch('/api/ai-review/chat-pdf', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as any).error || `PDF generation failed (${res.status})`);
  }
  return res.blob();
}

export async function loadCrossRef(returnId: string): Promise<ChatAttachment> {
  return apiFetch('/api/ai-review/chat-cross-ref', {
    method:  'POST',
    headers: { 'content-type': 'application/json' },
    body:    JSON.stringify({ returnId }),
  });
}

export class ReturnInUseError extends Error {
  lastUpdatedBy: string;
  constructor(lastUpdatedBy: string) {
    const who = lastUpdatedBy || 'another user';
    super(`The return is currently in use by ${who}. Please ask them to close it in CCH and try again.`);
    this.name = 'ReturnInUseError';
    this.lastUpdatedBy = lastUpdatedBy;
  }
}

export async function loadReturn(returnId: string): Promise<ChatLoadResult> {
  const res = await fetch('/api/ai-review/chat-load', {
    method:  'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body:    JSON.stringify({ returnId }),
  });
  if (res.status === 401) {
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/.auth/login/aad?domain_hint=bmss.com&post_login_redirect_uri=${returnTo}`;
    return new Promise(() => {});
  }
  if (res.status === 409) {
    const body = await res.json().catch(() => ({}));
    throw new ReturnInUseError((body as any).lastUpdatedBy || '');
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as any).error || `Server error ${res.status}`);
  }
  return res.json();
}

export interface ChatAttachment {
  filename: string;
  type:     'text' | 'image';
  content:  string;
  mimeType?: string;
  truncated?: boolean;
}

export async function uploadChatFile(file: File): Promise<ChatAttachment> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch('/api/ai-review/chat-upload', {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (res.status === 401) {
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/.auth/login/aad?domain_hint=bmss.com&post_login_redirect_uri=${returnTo}`;
    return new Promise(() => {});
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as any).error || `Upload failed (${res.status})`);
  }
  return res.json();
}

export async function sendMessage(
  returnId:       string,
  messages:       ChatMessage[],
  preprocessMode: 'auto' | 'full' = 'auto',
  introMode:      boolean = false,
  attachments:    ChatAttachment[] = [],
): Promise<ChatMessageResult> {
  return apiFetch('/api/ai-review/chat-message', {
    method:  'POST',
    headers: { 'content-type': 'application/json' },
    body:    JSON.stringify({ returnId, messages, preprocessMode, introMode, attachments }),
  });
}
